import './navbar.scss'

import _ from 'lodash'
import { Component, Watch } from 'vue-property-decorator'
import { faTimes, faUser } from '@fortawesome/free-solid-svg-icons'
import * as emojiFlags from 'emoji-flags'

import { User } from '@skinsoft/types'
import { workspaceSelectors, userSelectors } from '@skinsoft/selectors'
import { Proxy } from '@skinsoft/network'
import { AuthService, UserAuthenticatedResult } from '@skinsoft/auth'
import { TranslationDataStore, WorkspaceDataStore } from '@skinsoft/entity'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { Touchable } from '@skinsoft/ui'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus'
import { ContextMenuModal } from './context-menu-modal'

declare const config
const TIMEOUT_DURATION: number = config.touchable_component.navbar ?? 500

export const DEFAULT_PROXY = new Proxy()

@Component({
    components: {
        sContextMenuModal: ContextMenuModal,
        sTouchable: Touchable,
    },
    props: ['title'],
    template: require('./navbar.html'),
})
export class Navbar extends ReactiveVue {
    public $authService: AuthService
    public title: string

    public $refs: {
        modal: ContextMenuModal
        navbar: HTMLElement
    }

    public user: User | null = null
    public userIcon = faUser
    public logOutIcon = faTimes
    public userSelectors = userSelectors
    private localeFlag: string | null = null
    private availableLocales: Array<{ key: string; flag: string }> = []

    private $workspaceDataStore: WorkspaceDataStore
    private $translationDataStore: TranslationDataStore

    public workspaceUserInfo: any = {
        selectedWorkspace: {},
        workspaceList: [],
    }
    public onlineMode = true
    protected proxy = DEFAULT_PROXY

    TIMEOUT_DURATION = TIMEOUT_DURATION

    public created() {
        this.subscribeTo(this.$authService.waitForUser$(), this.onUser)
        this.checkOnLine()
        this.$translationDataStore
            .filterLanguageListWithCacheContent(Object.keys(this.$i18n.messages))
            .subscribe(
                localeList =>
                    (this.availableLocales = localeList.map(key => ({
                        key,
                        flag: emojiFlags.countryCode(this.$i18n.messages[key].countryCode || key),
                    }))),
            )
    }

    public logOut() {
        if (!confirm(this.$t('navbar.logout') as string)) {
            return
        }

        return this.$authService.logOut()
    }

    public getWorkspaceName(workspace) {
        return workspaceSelectors.name(workspace, this.getAppLocale())
    }

    public onTouchTimed = () => {
        this.$refs.modal.open()
    }

    private onUser(userAuthResult: UserAuthenticatedResult | null) {
        this.user = userAuthResult?.user ?? null
        if (userAuthResult?.user) {
            this.$workspaceDataStore.workspaceList = userSelectors.userWorkspaces(userAuthResult.user)
            if (localStorage.getItem('workspace')) {
                this.$workspaceDataStore.setCurrentWorkspace(localStorage.getItem('workspace')!)
                    .subscribe((success: boolean) => {
                        if(!success) {
                            const alertPayload = {
                                message: this.$t('alerts.workspace.unableToLoadWorkspace', this.$i18n.locale),
                                variant: "danger"
                            };
                            this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        }
                    })
            } else {
                this.$workspaceDataStore.setCurrentWorkspace(userSelectors.defaultWorkspace(userAuthResult.user).id)
                    .subscribe((success: boolean) => {
                        if(!success) {
                            const alertPayload = {
                                message: this.$t('alerts.workspace.unableToLoadWorkspace', this.$i18n.locale),
                                variant: "danger"
                            };
                            this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        }
                    })
            }
        }
    }

    private get currentWorkspace(): string {
        return this.$workspaceDataStore.currentWorkspace
    }

    private set currentWorkspace(id: string) {

        this.$workspaceDataStore.setCurrentWorkspace(id).subscribe((success: boolean) => {
            if(!success) {
                const alertPayload = {
                    message: this.$t('alerts.workspace.unableToChangeWorkspace', this.$i18n.locale),
                    variant: "danger"
                };
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                setTimeout(() => {
                    this.$forceUpdate()
                }, 850)
            }
        })
    }

    public get currentWorkspaceInfo() {
        return this.$workspaceDataStore.currentWorkspaceInfo
    }

    private get workspaceList() {
        return this.$workspaceDataStore.workspaceList
    }

    @Watch('$i18n.locale', { immediate: true })
    private onLocaleChange() {
        // If the locale name doesn't correspond to the country code, specify it in the locale file (ex : fr-ca => ca)
        const countryCode = this.$t('countryCode')
        const localeId = countryCode !== 'countryCode' ? countryCode : this.$i18n.locale
        const code = emojiFlags.countryCode(localeId)
        this.localeFlag = code ? code.emoji : null
    }

    private checkOnLine() {
        this.onlineMode = window.navigator.onLine
        requestAnimationFrame(this.checkOnLine)
    }

    private changeLocale(localeKey: string) {
        this.setAppLocale(localeKey)
    }
}
